import React, { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Edit3, Save, X, Plus, ChevronDown, ChevronUp, Trash2,
  User, Mail, Phone, Briefcase, Calendar, Target, MapPin, Hash,
  MessageSquare, ClipboardList, BookOpen, Video,
  Brain, BarChart3, AlertTriangle, CheckCircle2, ArrowRight,
  FileText, Receipt, Send, Download, Bell, Eye, Upload, GripVertical, Globe, Cloud, ExternalLink,
  Lock, Filter, Clock,
} from 'lucide-react';
import { clientsApi, casesApi, processApi, invoicesApi, documentsApi, sessionsApi, portalApi, integrationsApi, packagesApi } from '../services/api';
import CloudFilePicker from '../components/CloudFilePicker';
import BlockContent from '../components/blocks/BlockContent';
import { SessionForm } from '../components/blocks/SessionForm';
import VertragsRenderer from '../components/Vertrag/VertragsRenderer';
import { contractTemplatesApi } from '../services/api';
import { useToast } from '../context/ToastContext';
import { Skeleton, SkeletonCard, SkeletonListItem } from '../components/Skeleton';
import './Pages.css';

// --- Types (matching backend schemas) ---

interface Client {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  status: string;
  profession: string | null;
  street: string | null;
  zip_code: string | null;
  city: string | null;
  country: string | null;
  tax_number: string | null;
  notes: string | null;
  created_at: string;
}

interface ProcessBlock {
  id: string;
  case_id: string;
  module_type: string;
  module_name: string;
  order_position: number;
  description: string | null;
  status: string;
  start_date: string | null;
  completion_date: string | null;
  content_data: any;
  notes: string | null;
  version: number;
  sent_at: string | null;
  sent_version: number | null;
  session_id: string | null;
  session_number: number | null;
  session_scheduled_at: string | null;
  session_status: string | null;
  created_at: string;
  updated_at: string | null;
}

interface CaseWithBlocks {
  id: string;
  coach_id: string;
  case_number: string;
  case_type: string;
  title: string;
  description: string | null;
  status: string;
  priority: string | null;
  start_date: string | null;
  end_date: string | null;
  estimated_sessions: number | null;
  actual_sessions: number;
  created_at: string;
  blocks: ProcessBlock[];
  sessions_count: number;
  next_session: string | null;
}

interface InvoicesSummary {
  total: number;
  open: number;
  total_eur: number;
}

interface ProcessStats {
  total_sessions: number;
  completed_sessions: number;
  total_blocks: number;
  completed_blocks: number;
}

interface ClientProcessData {
  client: Client;
  cases: CaseWithBlocks[];
  invoices_summary: InvoicesSummary;
  stats: ProcessStats;
}

interface Invoice {
  id: string;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  subject: string | null;
  total_eur: number;
  status: string;
}

// Simple Case type for fallback
interface Case {
  id: string;
  case_number: string;
  case_type: string;
  title: string;
  status: string;
  client_id: string | null;
  actual_sessions: number;
  estimated_sessions: number | null;
  goals: string | null;
  presenting_problem: string | null;
  created_at: string;
}

// --- Constants ---

const COACHING_TOPICS = [
  'Führungskräfteentwicklung',
  'Berufliche Neuorientierung',
  'Work-Life-Balance',
  'Konfliktmanagement',
  'Stressmanagement & Burnout-Prävention',
  'Karriereplanung',
  'Teamführung & Teamentwicklung',
  'Kommunikation & Auftreten',
  'Persönlichkeitsentwicklung',
  'Selbstmanagement & Zeitmanagement',
  'Entscheidungsfindung',
  'Resilienz',
  'Onboarding / Neue Rolle',
  'Unternehmensgründung',
];

const clientStatusLabels: Record<string, string> = {
  active: 'Aktiv',
  paused: 'Pausiert',
  completed: 'Abgeschlossen',
  archived: 'Archiviert',
};

const invoiceStatusLabels: Record<string, string> = {
  draft: 'Entwurf',
  sent: 'Gesendet',
  paid: 'Bezahlt',
  partially_paid: 'Teilbezahlt',
  overdue: 'Überfällig',
  cancelled: 'Storniert',
};

const invoiceStatusBadge: Record<string, string> = {
  draft: 'badge-new',
  sent: 'badge-ongoing',
  paid: 'badge-active',
  partially_paid: 'badge-ongoing',
  overdue: 'badge-paused',
  cancelled: 'badge-completed',
};

const blockStatusLabels: Record<string, string> = {
  pending: 'Geplant',
  in_progress: 'In Bearbeitung',
  completed: 'Abgeschlossen',
};

const BLOCK_TYPES: Record<string, { label: string; color: string; icon: React.ComponentType<any> }> = {
  erstgespraech: { label: 'Erstgespräch / Anamnese', color: '#5B8C5A', icon: MessageSquare },
  zieldefinition: { label: 'Zieldefinition / Auftragsklärung', color: '#B8860B', icon: Target },
  session: { label: 'Coaching-Session / Intervention', color: '#5B8C5A', icon: BookOpen },
  reflexion: { label: 'Reflexion / Nachbereitung', color: '#CD853F', icon: Brain },
  review: { label: 'Zwischenbilanz / Review', color: '#5B7EA8', icon: BarChart3 },
  krisenintervention: { label: 'Krisenintervention', color: '#CD5C5C', icon: AlertTriangle },
  abschluss: { label: 'Abschluss & Transfer', color: '#2E8B57', icon: CheckCircle2 },
  followup: { label: 'Follow-Up', color: '#708090', icon: ArrowRight },
};

// --- Helpers ---

const fmtDate = (d: string) => new Date(d).toLocaleDateString('de-DE');
const fmtEur = (n: number) => n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

// --- Main Component ---

export default function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();
  const validTabs = ['profil', 'prozess', 'dokumente', 'rechnungen', 'pakete', 'portal'] as const;
  const tabParam = searchParams.get('tab') as typeof validTabs[number] | null;
  const [activeTab, setActiveTab] = useState<typeof validTabs[number]>(
    tabParam && validTabs.includes(tabParam) ? tabParam : 'profil'
  );
  const [processData, setProcessData] = useState<ClientProcessData | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [cases, setCases] = useState<Case[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showContract, setShowContract] = useState(false);
  const [contractTemplate, setContractTemplate] = useState<any>(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [docRefreshKey, setDocRefreshKey] = useState(0);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const loadProcessData = async () => {
    if (!id) return;
    try {
      const res = await processApi.clientDetail(id);
      setProcessData(res.data);
      setClient(res.data.client);
    } catch {
      // Fallback: load client separately
      try {
        const res = await clientsApi.get(id);
        setClient(res.data);
      } catch {
        setError('Klient konnte nicht geladen werden.');
      }
    }
  };

  const loadCases = async () => {
    if (!id) return;
    try {
      const res = await casesApi.list({ client_id: id });
      setCases(res.data.items || []);
    } catch { /* ignore */ }
  };

  const loadInvoices = async () => {
    if (!id) return;
    try {
      const res = await invoicesApi.list({ client_id: id });
      setInvoices(res.data.items || []);
    } catch { /* ignore */ }
  };

  const loadSessions = async () => {
    if (!id) return;
    try {
      // Lade alle Cases des Klienten, dann alle Sessions pro Case
      const casesRes = await casesApi.list({ client_id: id });
      const clientCases = casesRes.data.items || [];
      const allSessions: any[] = [];
      for (const c of clientCases) {
        const sessRes = await sessionsApi.list({ case_id: c.id });
        const items = (sessRes.data.items || []).map((s: any) => ({
          ...s,
          case_title: c.title,
          case_number: c.case_number,
        }));
        allSessions.push(...items);
      }
      // Sortiere nach Datum (neueste zuerst)
      allSessions.sort((a, b) => {
        if (!a.scheduled_at && !b.scheduled_at) return 0;
        if (!a.scheduled_at) return 1;
        if (!b.scheduled_at) return -1;
        return new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime();
      });
      setSessions(allSessions);
    } catch { /* ignore */ }
  };

  const reload = async () => {
    await Promise.all([loadProcessData(), loadCases(), loadInvoices(), loadSessions()]);
  };

  useEffect(() => {
    setLoading(true);
    reload().finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    setUploading(true);
    try {
      await documentsApi.uploadFile(file, id, undefined, 'contract');
      showToast('Vorlage wurde hochgeladen und in der Dokumentenbibliothek gespeichert.', 'success');
      setActiveTab('dokumente');
      setDocRefreshKey(k => k + 1);
      reload();
    } catch {
      showToast('Upload fehlgeschlagen.', 'error');
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  if (loading) {
    return (
      <div>
        <Skeleton width={100} height={14} style={{ marginBottom: 16 }} />
        <div style={{ display: 'flex', gap: 24, marginBottom: 24 }}>
          <div style={{ flex: 1 }}>
            <SkeletonCard />
          </div>
          <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Skeleton width="50%" height={28} />
            <Skeleton width="30%" height={14} />
            <div style={{ display: 'flex', gap: 16 }}>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <SkeletonListItem />
          <SkeletonListItem />
          <SkeletonListItem />
        </div>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div>
        <Link to="/klienten" className="btn btn-secondary mb-16">
          <ArrowLeft size={18} /> Zurück
        </Link>
        <div className="empty-state card">
          <p>{error || 'Klient nicht gefunden.'}</p>
        </div>
      </div>
    );
  }

  // Merge cases: use processData.cases (with blocks) if available, else plain cases
  const casesWithBlocks: CaseWithBlocks[] = processData?.cases || [];
  const stats = processData?.stats;
  const invSummary = processData?.invoices_summary;

  const tabs = [
    { key: 'profil' as const, label: 'Profil' },
    { key: 'prozess' as const, label: 'Prozessmappe' },
    { key: 'dokumente' as const, label: 'Dokumente' },
    { key: 'rechnungen' as const, label: 'Rechnungen' },
    { key: 'pakete' as const, label: 'Pakete' },
    { key: 'portal' as const, label: 'Portal' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex-between mb-24">
        <div className="flex gap-16" style={{ alignItems: 'center' }}>
          <Link to="/klienten" className="btn btn-secondary btn-sm">
            <ArrowLeft size={16} />
          </Link>
          <div>
            <h1>{client.first_name} {client.last_name}</h1>
            <p className="text-muted text-sm">
              <span className={`badge badge-${client.status}`}>
                {clientStatusLabels[client.status] || client.status}
              </span>
              {client.profession && <span style={{ marginLeft: 8 }}>{client.profession}</span>}
              {stats && stats.total_sessions > 0 && (
                <span style={{ marginLeft: 12 }}>
                  {stats.completed_sessions}/{stats.total_sessions} Sitzungen
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex gap-8" style={{ position: 'relative' }}>
          <button
            className="btn btn-primary"
            disabled={loadingTemplate}
            onClick={async () => {
              setLoadingTemplate(true);
              try {
                const res = await contractTemplatesApi.list();
                const templates = res.data.items || [];
                if (templates.length === 0) {
                  showToast('Keine Vertragsvorlage gefunden. Bitte erstelle eine unter Einstellungen \u2192 Vertragsvorlagen.', 'info');
                } else if (templates.length === 1) {
                  // Nur eine Vorlage → direkt verwenden
                  const tmpl = templates[0];
                  if (typeof tmpl.sections === 'string') tmpl.sections = JSON.parse(tmpl.sections);
                  setContractTemplate(tmpl);
                  setShowContract(true);
                } else {
                  // Mehrere Vorlagen → Auswahl zeigen
                  setAvailableTemplates(templates);
                  setShowTemplateSelector(true);
                }
              } catch {
                showToast('Vertragsvorlagen konnten nicht geladen werden.', 'error');
              }
              setLoadingTemplate(false);
            }}
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <FileText size={16} /> {loadingTemplate ? 'Wird geladen...' : 'Coaching-Vertrag erstellen'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <Upload size={16} /> {uploading ? 'Wird hochgeladen...' : 'Eigene Vorlage hochladen'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.html,.htm"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />

          {/* Template Selector Dropdown */}
          {showTemplateSelector && availableTemplates.length > 0 && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, marginTop: 8, zIndex: 100,
              background: 'var(--bg-card)', borderRadius: 'var(--radius)', boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              border: '1px solid var(--border-light)', minWidth: 320, overflow: 'hidden',
            }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ fontSize: '0.9rem' }}>Vorlage wählen</strong>
                <button className="btn-icon" onClick={() => setShowTemplateSelector(false)} style={{ padding: 4 }}>
                  <X size={16} />
                </button>
              </div>
              {availableTemplates.map((tmpl) => (
                <button
                  key={tmpl.id}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', padding: '12px 16px',
                    border: 'none', background: 'none', cursor: 'pointer', borderBottom: '1px solid var(--border-light)',
                    fontSize: '0.88rem',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                  onClick={() => {
                    const t = { ...tmpl };
                    if (typeof t.sections === 'string') t.sections = JSON.parse(t.sections);
                    setContractTemplate(t);
                    setShowContract(true);
                    setShowTemplateSelector(false);
                  }}
                >
                  <div style={{ fontWeight: 500 }}>
                    {tmpl.name}
                    {tmpl.is_default && <span className="badge badge-ongoing" style={{ marginLeft: 8, fontSize: '0.7rem' }}>Standard</span>}
                    {tmpl.is_system && <span className="badge badge-new" style={{ marginLeft: 8, fontSize: '0.7rem' }}>System</span>}
                  </div>
                  {tmpl.description && (
                    <div className="text-muted text-sm" style={{ marginTop: 2 }}>{tmpl.description}</div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="category-chips mb-24">
        {tabs.map((t) => (
          <button
            key={t.key}
            className={`chip ${activeTab === t.key ? 'chip-active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Coaching-Vertrag Overlay */}
      {showContract && contractTemplate && (
        <div style={{ marginBottom: 24 }}>
          <VertragsRenderer
            template={contractTemplate}
            clientId={id}
            onSaved={() => {
              setShowContract(false);
              setActiveTab('dokumente');
              setDocRefreshKey(k => k + 1);
            }}
            onClose={() => setShowContract(false)}
          />
        </div>
      )}
      {showContract && loadingTemplate && (
        <div className="card" style={{ padding: 24, textAlign: 'center', marginBottom: 24, color: 'var(--text-muted)' }}>
          Vertragsvorlage wird geladen...
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'profil' && (
        <ProfilTab client={client} cases={cases} sessions={sessions} onClientUpdated={reload} />
      )}
      {activeTab === 'prozess' && (
        <ProzessTab
          client={client}
          casesWithBlocks={casesWithBlocks}
          plainCases={cases}
          stats={stats}
          onChanged={reload}
        />
      )}
      {activeTab === 'dokumente' && <DokumenteTab clientId={id!} refreshKey={docRefreshKey} />}
      {activeTab === 'rechnungen' && (
        <RechnungenTab clientId={id!} invoices={invoices} summary={invSummary} onReload={reload} />
      )}
      {activeTab === 'pakete' && <PaketeTab clientId={id!} />}
      {activeTab === 'portal' && <PortalTab clientId={id!} clientEmail={client.email} />}
    </div>
  );
}



// ============================================================
// Tab: Profil
// ============================================================

function ProfilTab({
  client,
  cases,
  sessions,
  onClientUpdated,
}: {
  client: Client;
  cases: Case[];
  sessions: any[];
  onClientUpdated: () => void;
}) {
  const { showToast } = useToast();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    first_name: client.first_name,
    last_name: client.last_name,
    email: client.email || '',
    phone: client.phone || '',
    profession: client.profession || '',
    street: client.street || '',
    zip_code: client.zip_code || '',
    city: client.city || '',
    country: client.country || '',
    tax_number: client.tax_number || '',
    notes: client.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      await clientsApi.update(client.id, {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email || null,
        phone: form.phone || null,
        profession: form.profession || null,
        street: form.street || null,
        zip_code: form.zip_code || null,
        city: form.city || null,
        country: form.country || null,
        tax_number: form.tax_number || null,
        notes: form.notes || null,
      });
      onClientUpdated();
      setEditing(false);
    } catch (err: any) {
      setSaveError(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({
      first_name: client.first_name,
      last_name: client.last_name,
      email: client.email || '',
      phone: client.phone || '',
      profession: client.profession || '',
      street: client.street || '',
      zip_code: client.zip_code || '',
      city: client.city || '',
      country: client.country || '',
      tax_number: client.tax_number || '',
      notes: client.notes || '',
    });
    setEditing(false);
    setSaveError('');
  };

  return (
    <div className="card">
      <div className="flex-between mb-16">
        <h3>Kontaktdaten</h3>
        {!editing ? (
          <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>
            <Edit3 size={14} /> Bearbeiten
          </button>
        ) : (
          <div className="flex gap-8">
            <button className="btn btn-secondary btn-sm" onClick={handleCancel}>
              <X size={14} /> Abbrechen
            </button>
            <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
              <Save size={14} /> {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        )}
      </div>

      {saveError && <div className="auth-error mb-16">{saveError}</div>}

      {!editing ? (
        <div className="profil-info-grid">
          <div className="profil-info-item">
            <User size={16} className="profil-info-icon" />
            <div>
              <span className="profil-info-label">Name</span>
              <span className="profil-info-value">{client.first_name} {client.last_name}</span>
            </div>
          </div>
          <div className="profil-info-item">
            <Mail size={16} className="profil-info-icon" />
            <div>
              <span className="profil-info-label">E-Mail</span>
              <span className="profil-info-value">{client.email || '---'}</span>
            </div>
          </div>
          <div className="profil-info-item">
            <Phone size={16} className="profil-info-icon" />
            <div>
              <span className="profil-info-label">Telefon</span>
              <span className="profil-info-value">{client.phone || '---'}</span>
            </div>
          </div>
          <div className="profil-info-item">
            <Briefcase size={16} className="profil-info-icon" />
            <div>
              <span className="profil-info-label">Beruf</span>
              <span className="profil-info-value">{client.profession || '---'}</span>
            </div>
          </div>
          <div className="profil-info-item">
            <MapPin size={16} className="profil-info-icon" />
            <div>
              <span className="profil-info-label">Adresse</span>
              <span className="profil-info-value">
                {client.street || client.city || client.zip_code
                  ? [client.street, [client.zip_code, client.city].filter(Boolean).join(' '), client.country].filter(Boolean).join(', ')
                  : '---'}
              </span>
            </div>
          </div>
          {client.tax_number && (
            <div className="profil-info-item">
              <Hash size={16} className="profil-info-icon" />
              <div>
                <span className="profil-info-label">Steuernummer</span>
                <span className="profil-info-value">{client.tax_number}</span>
              </div>
            </div>
          )}
          <div className="profil-info-item">
            <Calendar size={16} className="profil-info-icon" />
            <div>
              <span className="profil-info-label">Erstellt am</span>
              <span className="profil-info-value">{fmtDate(client.created_at)}</span>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="form-row">
            <div className="form-group">
              <label>Vorname</label>
              <input name="first_name" value={form.first_name} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Nachname</label>
              <input name="last_name" value={form.last_name} onChange={handleChange} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>E-Mail</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Telefon</label>
              <input name="phone" value={form.phone} onChange={handleChange} />
            </div>
          </div>
          <div className="form-group">
            <label>Beruf</label>
            <input name="profession" value={form.profession} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Straße</label>
            <input name="street" value={form.street} onChange={handleChange} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>PLZ</label>
              <input name="zip_code" value={form.zip_code} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Ort</label>
              <input name="city" value={form.city} onChange={handleChange} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Land</label>
              <input name="country" value={form.country} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Steuernummer</label>
              <input name="tax_number" value={form.tax_number} onChange={handleChange} placeholder="Für Businesskunden" />
            </div>
          </div>
          <div className="form-group">
            <label>Notizen</label>
            <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} />
          </div>
        </div>
      )}

      {/* Sitzungsübersicht (Zusammenfassung) */}
      {sessions.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h3 className="mb-8">Sitzungen</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 }}>
            <div style={{ background: 'var(--bg)', borderRadius: 'var(--radius-sm)', padding: '12px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--primary)' }}>{sessions.length}</div>
              <div className="text-muted text-sm">Gesamt</div>
            </div>
            <div style={{ background: 'var(--bg)', borderRadius: 'var(--radius-sm)', padding: '12px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--success, #2E8B57)' }}>{sessions.filter(s => s.status === 'completed').length}</div>
              <div className="text-muted text-sm">Abgeschlossen</div>
            </div>
            <div style={{ background: 'var(--bg)', borderRadius: 'var(--radius-sm)', padding: '12px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--accent)' }}>{sessions.filter(s => s.status === 'scheduled' || s.status === 'in_progress').length}</div>
              <div className="text-muted text-sm">Geplant / Laufend</div>
            </div>
          </div>
          <p className="text-muted text-sm" style={{ marginTop: 8 }}>
            Details zu Sitzungen findest du im <strong>Prozessmappe</strong>-Tab.
          </p>
        </div>
      )}
      {client.notes && !editing && (
        <div style={{ marginTop: 24 }}>
          <h3 className="mb-8">Notizen</h3>
          <div className="card" style={{ padding: 16, background: 'var(--bg)', whiteSpace: 'pre-wrap', fontSize: '0.9rem', color: 'var(--text-light)' }}>
            {client.notes}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Tab: Prozessmappe (Baumstruktur: Fälle → Sitzungen + Bausteine)
// ============================================================

interface SessionItem {
  id: string;
  session_number: number;
  scheduled_at: string | null;
  duration_minutes: number | null;
  status: string;
  location_type: string;
  session_type: string;
  content_data: any;
  topics_discussed: string | null;
  homework_assigned: string | null;
  coach_observations: string | null;
  next_session_notes: string | null;
}

const caseStatusLabels: Record<string, string> = {
  new: 'Neu',
  ongoing: 'Laufend',
  completed: 'Abgeschlossen',
  archived: 'Archiviert',
};

const caseStatusBadge: Record<string, string> = {
  new: 'badge-new',
  ongoing: 'badge-ongoing',
  completed: 'badge-active',
  archived: 'badge-completed',
};

const sessionStatusLabels: Record<string, string> = {
  scheduled: 'Geplant',
  in_progress: 'In Bearbeitung',
  completed: 'Abgeschlossen',
  cancelled: 'Abgesagt',
  no_show: 'Nicht erschienen',
};

function ProzessTab({
  client,
  casesWithBlocks,
  plainCases,
  stats,
  onChanged,
}: {
  client: Client;
  casesWithBlocks: CaseWithBlocks[];
  plainCases: Case[];
  stats: ProcessStats | undefined;
  onChanged: () => void;
}) {
  const { showToast } = useToast();

  // Which cases are expanded (accordion) — use array (React tracks array changes better than Set)
  const firstActiveCase = casesWithBlocks.find((c) => c.status === 'ongoing' || c.status === 'new') || casesWithBlocks[0];
  const [expandedCases, setExpandedCases] = useState<string[]>(
    firstActiveCase ? [firstActiveCase.id] : []
  );

  // Lazy-loaded sessions per case
  const [caseSessions, setCaseSessions] = useState<Record<string, SessionItem[]>>({});
  const [loadingSessions, setLoadingSessions] = useState<Record<string, boolean>>({});
  const loadedCasesRef = React.useRef<Set<string>>(new Set());

  // Block management
  const [showBlockSelectorFor, setShowBlockSelectorFor] = useState<string | null>(null);
  const [expandedBlock, setExpandedBlock] = useState<string | null>(null);
  const [editingNotes, setEditingNotes] = useState<string | null>(null);
  const [notesValue, setNotesValue] = useState('');
  const [creatingCase, setCreatingCase] = useState(false);
  const [showNewCaseForm, setShowNewCaseForm] = useState(false);
  const [newCaseTopic, setNewCaseTopic] = useState('');
  const [newCaseCustom, setNewCaseCustom] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewBlockId, setPreviewBlockId] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Details section per case
  const [showDetailsFor, setShowDetailsFor] = useState<string[]>([]);

  // Open session protocol
  const [openSession, setOpenSession] = useState<SessionItem | null>(null);

  // Drag & Drop reorder
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const handleDragStart = (e: React.DragEvent, blockId: string) => {
    setDragId(blockId);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragEnd = () => { setDragId(null); setDragOverId(null); };
  const handleDragOver = (e: React.DragEvent, blockId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (blockId !== dragId) setDragOverId(blockId);
  };
  const handleDragLeave = () => setDragOverId(null);
  const handleDrop = async (e: React.DragEvent, targetId: string, caseId: string, blocks: ProcessBlock[]) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) { handleDragEnd(); return; }
    const blockIds = blocks.map((b) => b.id);
    const fromIdx = blockIds.indexOf(dragId);
    const toIdx = blockIds.indexOf(targetId);
    if (fromIdx === -1 || toIdx === -1) { handleDragEnd(); return; }
    const newOrder = [...blockIds];
    newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, dragId);
    handleDragEnd();
    try {
      await processApi.reorderBlocks(caseId, newOrder);
      await onChanged();
    } catch { /* silent */ }
  };

  // Load sessions for a case (lazy)
  const loadCaseSessions = async (caseId: string) => {
    if (loadedCasesRef.current.has(caseId)) return;
    loadedCasesRef.current.add(caseId);
    setLoadingSessions((prev) => ({ ...prev, [caseId]: true }));
    try {
      const res = await sessionsApi.list({ case_id: caseId });
      const items = (res.data.items || []) as SessionItem[];
      items.sort((a, b) => (a.session_number || 0) - (b.session_number || 0));
      setCaseSessions((prev) => ({ ...prev, [caseId]: items }));
    } catch {
      loadedCasesRef.current.delete(caseId);
    }
    setLoadingSessions((prev) => ({ ...prev, [caseId]: false }));
  };

  // Toggle case accordion
  const toggleCase = (caseId: string) => {
    setExpandedCases((prev) => {
      const isOpen = prev.includes(caseId);
      if (isOpen) {
        return prev.filter((id) => id !== caseId);
      } else {
        loadCaseSessions(caseId);
        return [...prev, caseId];
      }
    });
  };

  // Load sessions for initially expanded case
  useEffect(() => {
    if (firstActiveCase) {
      loadCaseSessions(firstActiveCase.id);
    }
  }, []); // eslint-disable-line

  const hasCases = casesWithBlocks.length > 0 || plainCases.length > 0;

  const handleCreateCase = async (topic?: string) => {
    const title = (topic || newCaseTopic).trim();
    if (!title) return;
    setCreatingCase(true);
    try {
      await casesApi.create({
        title,
        case_type: 'individual',
        client_id: client.id,
      });
      setShowNewCaseForm(false);
      setNewCaseTopic('');
      setNewCaseCustom(false);
      onChanged();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler beim Anlegen des Falls', 'error');
    }
    setCreatingCase(false);
  };

  const handleAddBlock = async (caseId: string, moduleType: string) => {
    try {
      await processApi.addBlock(caseId, { module_type: moduleType });
      setShowBlockSelectorFor(null);
      onChanged();
    } catch { /* ignore */ }
  };

  const handleUpdateBlockStatus = async (blockId: string, newStatus: string) => {
    try {
      await processApi.updateBlock(blockId, { status: newStatus });
      onChanged();
    } catch { /* ignore */ }
  };

  const handleSaveNotes = async (blockId: string) => {
    try {
      await processApi.updateBlock(blockId, { notes: notesValue });
      setEditingNotes(null);
      onChanged();
    } catch { /* ignore */ }
  };

  const handleDeleteBlock = async (blockId: string) => {
    if (!window.confirm('Baustein wirklich entfernen?')) return;
    try {
      await processApi.deleteBlock(blockId);
      onChanged();
    } catch { /* ignore */ }
  };

  // Shared "New Case" form
  const newCaseForm = (
    <div className="card" style={{ padding: 20 }}>
      <h4 style={{ margin: '0 0 12px' }}>Neuen Fall anlegen</h4>
      <div className="form-group" style={{ marginBottom: 12 }}>
        <label>Coaching-Thema *</label>
        <select
          value={newCaseCustom ? '__custom__' : newCaseTopic}
          onChange={(e) => {
            if (e.target.value === '__custom__') {
              setNewCaseCustom(true);
              setNewCaseTopic('');
            } else {
              setNewCaseCustom(false);
              setNewCaseTopic(e.target.value);
            }
          }}
        >
          <option value="">— Thema wählen —</option>
          {COACHING_TOPICS.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
          <option value="__custom__">Eigenes Thema eingeben...</option>
        </select>
        {newCaseCustom && (
          <input
            value={newCaseTopic}
            onChange={(e) => setNewCaseTopic(e.target.value)}
            placeholder="Eigenes Coaching-Thema eingeben..."
            autoFocus
            style={{ marginTop: 8 }}
          />
        )}
      </div>
      <div className="flex gap-8">
        <button
          className="btn btn-primary btn-sm"
          onClick={() => handleCreateCase()}
          disabled={creatingCase || !newCaseTopic.trim()}
        >
          <Plus size={14} /> {creatingCase ? 'Wird erstellt...' : 'Fall anlegen'}
        </button>
        {hasCases && (
          <button className="btn btn-secondary btn-sm" onClick={() => { setShowNewCaseForm(false); setNewCaseTopic(''); setNewCaseCustom(false); }}>
            Abbrechen
          </button>
        )}
      </div>
    </div>
  );

  // No case yet
  if (!hasCases) {
    return (
      <div>
        <div className="empty-state card" style={{ marginBottom: 16 }}>
          <Target size={48} style={{ color: 'var(--primary-light)', marginBottom: 16 }} />
          <h3 style={{ marginBottom: 8 }}>Noch kein Coaching-Prozess</h3>
          <p className="text-muted mb-16">
            Starte einen neuen Coaching-Prozess für {client.first_name} {client.last_name}.
          </p>
        </div>
        {newCaseForm}
      </div>
    );
  }

  // Global progress
  const allBlocks: ProcessBlock[] = casesWithBlocks.flatMap((c) => c.blocks || []);
  const completedCount = stats?.completed_blocks ?? allBlocks.filter((b) => b.status === 'completed').length;
  const totalCount = stats?.total_blocks ?? allBlocks.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div>
      {/* Global Progress Bar */}
      {totalCount > 0 && (
        <div className="card mb-16" style={{ padding: 16 }}>
          <div className="flex-between mb-8">
            <span className="text-sm" style={{ fontWeight: 500 }}>Gesamtfortschritt</span>
            <span className="text-sm text-muted">{completedCount} / {totalCount} Bausteine ({progressPercent}%)</span>
          </div>
          <div className="progress-bar">
            <div className="progress-bar-fill" style={{ width: `${progressPercent}%` }} />
          </div>
        </div>
      )}

      {/* Neuen Fall anlegen */}
      <div className="mb-16">
        {showNewCaseForm ? (
          newCaseForm
        ) : (
          <button className="btn btn-secondary" onClick={() => setShowNewCaseForm(true)}>
            <Plus size={16} /> Neuen Fall anlegen
          </button>
        )}
      </div>

      {/* Case Accordion Tree */}
      {casesWithBlocks.map((caseItem) => {
        const isExpanded = expandedCases.includes(caseItem.id);
        const sessions = caseSessions[caseItem.id] || [];
        const isLoadingSessions = loadingSessions[caseItem.id] || false;
        const blocks = caseItem.blocks || [];
        const showDetails = showDetailsFor.includes(caseItem.id);
        const completedSessions = sessions.filter((s) => s.status === 'completed').length;
        const caseCompletedBlocks = blocks.filter(b => b.status === 'completed').length;
        const caseProgressPct = blocks.length > 0 ? Math.round((caseCompletedBlocks / blocks.length) * 100) : 0;

        return (
          <div key={caseItem.id} className="card" style={{ marginBottom: 12, padding: 0, overflow: 'hidden' }}>
            {/* Case Header (klickbar zum Auf-/Zuklappen) */}
            <div
              onClick={() => toggleCase(caseItem.id)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '16px 20px',
                cursor: 'pointer',
                background: isExpanded ? 'var(--bg)' : 'transparent',
                borderBottom: isExpanded ? '1px solid var(--border-light)' : 'none',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => { if (!isExpanded) e.currentTarget.style.background = 'var(--bg)'; }}
              onMouseLeave={(e) => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{ marginTop: 3 }}>
                {isExpanded ? <ChevronDown size={18} /> : <ChevronUp size={18} style={{ transform: 'rotate(90deg)' }} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '1rem' }}>
                  {caseItem.title}
                  <span className="text-muted" style={{ fontWeight: 400, fontSize: '0.82rem', marginLeft: 8 }}>
                    ({caseItem.case_number})
                  </span>
                </div>
                {blocks.length > 0 && (
                  <div className="pm-case-header-progress" style={{ maxWidth: 280 }}>
                    <div className="pm-case-header-progress-fill" style={{ width: `${caseProgressPct}%` }} />
                  </div>
                )}
                <div className="pm-case-stats">
                  <span>{completedSessions}/{sessions.length} Sitzungen</span>
                  {blocks.length > 0 && <span> · {caseCompletedBlocks}/{blocks.length} Bausteine</span>}
                </div>
                {!isExpanded && caseItem.next_session && (
                  <div className="pm-case-next-session" style={{ marginTop: 6 }}>
                    <Calendar size={14} />
                    <span>Nächste Sitzung: {new Date(caseItem.next_session).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })} um {new Date(caseItem.next_session).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr</span>
                  </div>
                )}
              </div>
              <span className={`badge ${caseStatusBadge[caseItem.status] || ''}`} style={{ flexShrink: 0, marginTop: 2 }}>
                {caseStatusLabels[caseItem.status] || caseItem.status}
              </span>
            </div>

            {/* Case Body (aufgeklappt) */}
            {isExpanded && (
              <div style={{ padding: '16px 20px' }}>

                {/* Nächste Sitzung Banner */}
                {caseItem.next_session && (
                  <div className="pm-case-next-session mb-16">
                    <Calendar size={14} />
                    <span>
                      <strong>Nächste Sitzung:</strong>{' '}
                      {new Date(caseItem.next_session).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}{' '}
                      um {new Date(caseItem.next_session).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
                    </span>
                  </div>
                )}

                {/* === SITZUNGEN === */}
                <div className="mb-16">
                  <div className="flex-between mb-8">
                    <h4 style={{ margin: 0, fontSize: '0.95rem' }}>
                      Sitzungen
                      {sessions.length > 0 && (
                        <span className="text-muted" style={{ fontWeight: 400, marginLeft: 8, fontSize: '0.82rem' }}>
                          ({completedSessions}/{sessions.length}{caseItem.estimated_sessions ? ` von ${caseItem.estimated_sessions}` : ''})
                        </span>
                      )}
                    </h4>
                    <Link
                      to={`/sitzung-anlegen?case_id=${caseItem.id}&client=${client.id}`}
                      className="btn btn-primary btn-sm"
                    >
                      <Plus size={14} /> Neue Sitzung
                    </Link>
                  </div>

                  {isLoadingSessions ? (
                    <div className="text-muted text-sm" style={{ padding: '12px 0' }}>Sitzungen werden geladen...</div>
                  ) : sessions.length === 0 ? (
                    <div className="text-muted text-sm" style={{ padding: '8px 0' }}>Noch keine Sitzungen angelegt.</div>
                  ) : (
                    <SessionsList
                      sessions={sessions}
                      caseId={caseItem.id}
                      openSession={openSession}
                      setOpenSession={setOpenSession}
                      client={client}
                      showToast={showToast}
                      loadedCasesRef={loadedCasesRef}
                      loadCaseSessions={loadCaseSessions}
                      onChanged={onChanged}
                    />
                  )}
                </div>

                {/* === PROZESSBAUSTEINE === */}
                <div className="mb-16" style={{ borderTop: '1px solid var(--border-light)', paddingTop: 16 }}>
                  <div className="flex-between mb-8">
                    <h4 style={{ margin: 0, fontSize: '0.95rem' }}>
                      Prozessbausteine
                      {blocks.length > 0 && (
                        <span className="text-muted" style={{ fontWeight: 400, marginLeft: 8, fontSize: '0.82rem' }}>
                          ({blocks.filter((b) => b.status === 'completed').length}/{blocks.length})
                        </span>
                      )}
                    </h4>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => setShowBlockSelectorFor(showBlockSelectorFor === caseItem.id ? null : caseItem.id)}
                    >
                      <Plus size={14} /> Baustein
                    </button>
                  </div>

                  {blocks.length === 0 ? (
                    <div className="text-muted text-sm" style={{ padding: '8px 0' }}>Noch keine Bausteine angelegt.</div>
                  ) : (
                    <div className="process-timeline">
                      {blocks.map((block) => {
                        const blockInfo = BLOCK_TYPES[block.module_type] || {
                          label: block.module_name,
                          color: 'var(--primary, #8B7355)',
                          icon: FileText,
                        };
                        const Icon = blockInfo.icon;
                        const isBlockExpanded = expandedBlock === block.id;
                        const isEditingBlockNotes = editingNotes === block.id;
                        const isDragOver = dragOverId === block.id;

                        // Count filled fields in content_data
                        const contentFields = block.content_data ? Object.keys(block.content_data).filter(k => block.content_data[k]) : [];
                        const totalFields = block.content_data ? Object.keys(block.content_data).length : 0;

                        return (
                          <div
                            key={block.id}
                            id={`block-${block.id}`}
                            className={`process-block ${isBlockExpanded ? 'process-block-expanded' : ''} pm-block-status-${block.status}`}
                            style={{
                              borderTop: isDragOver ? '3px solid #4A90D9' : '3px solid transparent',
                              opacity: dragId === block.id ? 0.4 : 1,
                              transition: 'border-top 0.15s ease, opacity 0.15s ease',
                            }}
                            draggable={!isBlockExpanded}
                            onDragStart={(e) => handleDragStart(e, block.id)}
                            onDragEnd={handleDragEnd}
                            onDragOver={(e) => handleDragOver(e, block.id)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, block.id, caseItem.id, blocks)}
                          >
                            <div
                              className="process-block-header"
                              onClick={() => {
                                setExpandedBlock(isBlockExpanded ? null : block.id);
                                if (!isBlockExpanded && block.notes) setNotesValue(block.notes);
                              }}
                            >
                              <div
                                className="drag-handle"
                                style={{
                                  cursor: isBlockExpanded ? 'default' : 'grab',
                                  opacity: 0.3, transition: 'opacity 0.15s',
                                  display: 'flex', alignItems: 'center', padding: '0 4px',
                                  color: 'var(--text-muted)', flexShrink: 0,
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                <GripVertical size={16} />
                              </div>
                              <div className="process-block-icon" style={{ background: blockInfo.color }}>
                                <Icon size={16} color="white" />
                              </div>
                              <div className="process-block-info">
                                <span className="process-block-title">{block.module_name}</span>
                                <div className="pm-block-meta-info">
                                  {block.module_type === 'session' && block.session_id ? (
                                    <>
                                      {block.session_number && <span>Nr. {block.session_number}</span>}
                                      {block.session_scheduled_at && (
                                        <span><Calendar size={11} /> {fmtDate(block.session_scheduled_at)}, {new Date(block.session_scheduled_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span>
                                      )}
                                      {!block.session_scheduled_at && <span style={{ color: 'var(--warning, #B8860B)' }}>Noch kein Termin</span>}
                                    </>
                                  ) : block.module_type === 'session' && !block.session_id ? (
                                    <span style={{ color: 'var(--warning, #B8860B)' }}>Nicht verknüpft</span>
                                  ) : (
                                    <>
                                      {totalFields > 0 && <span>{contentFields.length} von {totalFields} Feldern</span>}
                                      {block.updated_at && <span> · Zuletzt: {fmtDate(block.updated_at)}</span>}
                                      {block.start_date && !block.updated_at && <span><Calendar size={11} /> {fmtDate(block.start_date)}</span>}
                                    </>
                                  )}
                                </div>
                              </div>
                              <div className="flex gap-8" style={{ alignItems: 'center' }}>
                                <span className={`badge process-status-badge process-status-${block.status}`}>
                                  {blockStatusLabels[block.status] || block.status}
                                </span>
                                {isBlockExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                              </div>
                            </div>

                            {isBlockExpanded && (
                              <div className="process-block-body">
                                <div className="mb-16">
                                  <label>Status</label>
                                  <div className="flex gap-8" style={{ marginTop: 4 }}>
                                    {Object.entries(blockStatusLabels).map(([key, label]) => (
                                      <button
                                        key={key}
                                        className={`btn btn-sm ${block.status === key ? 'btn-primary' : 'btn-secondary'}`}
                                        onClick={() => handleUpdateBlockStatus(block.id, key)}
                                      >
                                        {label}
                                      </button>
                                    ))}
                                  </div>
                                </div>

                                {block.description && (
                                  <div className="mb-16">
                                    <label>Beschreibung</label>
                                    <div style={{ background: 'var(--bg)', borderRadius: 'var(--radius-sm)', padding: 12, fontSize: '0.88rem', color: 'var(--text-light)' }}>
                                      {block.description}
                                    </div>
                                  </div>
                                )}

                                <div className="mb-16">
                                  <BlockContent
                                    blockId={block.id}
                                    caseId={caseItem.id}
                                    moduleType={block.module_type}
                                    contentData={block.content_data}
                                    clientEmail={client.email || ''}
                                    clientId={client.id}
                                    sessionId={block.session_id}
                                    sessionNumber={block.session_number}
                                    sessionScheduledAt={block.session_scheduled_at}
                                    sessionStatus={block.session_status}
                                    onSave={async (contentData) => {
                                      await processApi.updateBlock(block.id, { content_data: contentData });
                                      onChanged();
                                    }}
                                    onChanged={onChanged}
                                  />
                                </div>

                                <div className="mb-16" style={{ borderTop: '1px solid var(--border-light)', paddingTop: 16, marginTop: 8 }}>
                                  <div className="flex-between mb-8">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <label style={{ marginBottom: 0 }}>Notizen</label>
                                      <span className="pm-block-coach-only"><Lock size={10} /> Nur Coach</span>
                                    </div>
                                    {!isEditingBlockNotes ? (
                                      <button className="btn btn-sm btn-secondary" onClick={() => { setEditingNotes(block.id); setNotesValue(block.notes || ''); }}>
                                        <Edit3 size={12} /> Bearbeiten
                                      </button>
                                    ) : (
                                      <div className="flex gap-8">
                                        <button className="btn btn-sm btn-secondary" onClick={() => setEditingNotes(null)}><X size={12} /> Abbrechen</button>
                                        <button className="btn btn-sm btn-primary" onClick={() => handleSaveNotes(block.id)}><Save size={12} /> Speichern</button>
                                      </div>
                                    )}
                                  </div>
                                  {isEditingBlockNotes ? (
                                    <textarea value={notesValue} onChange={(e) => setNotesValue(e.target.value)} rows={4} placeholder="Notizen zum Baustein..." />
                                  ) : (
                                    <div style={{ background: 'var(--bg)', borderRadius: 'var(--radius-sm)', padding: 12, fontSize: '0.88rem', color: block.notes ? 'var(--text)' : 'var(--text-muted)', whiteSpace: 'pre-wrap', minHeight: 40 }}>
                                      {block.notes || 'Keine Notizen vorhanden.'}
                                    </div>
                                  )}
                                </div>

                                {block.version > 0 && (
                                  <div className="block-version-status" style={{ borderTop: '1px solid var(--border-light)', marginTop: 8 }}>
                                    <span className="block-version-badge">Version {block.version}</span>
                                    {block.sent_at && block.sent_version != null && (
                                      <>
                                        <span className="block-sent-badge">
                                          <Send size={12} />
                                          Gesendet am {new Date(block.sent_at).toLocaleDateString('de-DE')} (V{block.sent_version})
                                        </span>
                                        {block.version > block.sent_version && (
                                          <span className="block-unsent-badge">
                                            <AlertTriangle size={12} />
                                            Änderungen seit letztem Versand nicht gesendet
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </div>
                                )}

                                {client.email && block.module_type !== 'followup' && (
                                  <div className="mb-16" style={{ paddingTop: 8 }}>
                                    <button
                                      className="btn btn-sm btn-primary"
                                      onClick={async () => {
                                        setPreviewLoading(true);
                                        setPreviewBlockId(block.id);
                                        try {
                                          const res = await processApi.previewFillable(block.id);
                                          setPreviewHtml(res.data);
                                        } catch {
                                          showToast('Vorschau konnte nicht geladen werden.', 'error');
                                        } finally {
                                          setPreviewLoading(false);
                                        }
                                      }}
                                    >
                                      <Eye size={14} /> {previewLoading && previewBlockId === block.id ? 'Lade...' : 'Formular-Vorschau & Senden'}
                                    </button>
                                    <p className="text-muted" style={{ fontSize: '0.75rem', marginTop: 6, marginBottom: 0 }}>
                                      {block.module_type === 'reflexion'
                                        ? <>Klient erhält einen Feedbackbogen — Coach-Reflexion wird <strong>nicht</strong> mitgesendet.</>
                                        : block.module_type === 'abschluss'
                                        ? <>Klient erhält das Abschlussformular mit Zielen, Transferplan und Unterschriftsfeld.</>
                                        : block.module_type === 'erstgespraech' || block.module_type === 'anamnese'
                                        ? <>Klient erhält einen ausfüllbaren Fragebogen zur Vorbereitung.</>
                                        : block.module_type === 'session'
                                        ? <>Klient erhält ein Feedback-Formular zur Sitzung.</>
                                        : <>Klient erhält ein ausfüllbares Formular zu diesem Baustein.</>
                                      }
                                    </p>
                                  </div>
                                )}

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border-light)', paddingTop: 12 }}>
                                  <button className="btn btn-sm btn-secondary" onClick={() => setExpandedBlock(null)}>
                                    <ChevronUp size={12} /> Baustein schließen
                                  </button>
                                  <button className="btn btn-sm btn-danger" onClick={() => handleDeleteBlock(block.id)}>
                                    <Trash2 size={12} /> Baustein entfernen
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Block Selector */}
                  {showBlockSelectorFor === caseItem.id && (
                    <div className="card mt-16" style={{ padding: 0, overflow: 'hidden' }}>
                      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-light)' }}>
                        <div className="flex-between">
                          <h4 style={{ margin: 0 }}>Baustein wählen</h4>
                          <button className="btn-icon" onClick={() => setShowBlockSelectorFor(null)}><X size={18} /></button>
                        </div>
                      </div>
                      <div className="block-selector-grid">
                        {Object.entries(BLOCK_TYPES).map(([key, info]) => {
                          const BIcon = info.icon;
                          return (
                            <button key={key} className="block-selector-item" onClick={() => handleAddBlock(caseItem.id, key)}>
                              <div className="block-selector-icon" style={{ background: info.color }}><BIcon size={18} color="white" /></div>
                              <span>{info.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* === DETAILS (Beschreibung, Ziele, Zeitraum) === */}
                <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: 12 }}>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => {
                      setShowDetailsFor((prev) =>
                        prev.includes(caseItem.id)
                          ? prev.filter((id) => id !== caseItem.id)
                          : [...prev, caseItem.id]
                      );
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    {showDetails ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    Details
                  </button>
                  {showDetails && (
                    <div style={{ marginTop: 12, display: 'grid', gap: 12 }}>
                      {caseItem.description && (
                        <div>
                          <label className="text-sm text-muted">Beschreibung</label>
                          <div style={{ background: 'var(--bg)', borderRadius: 'var(--radius-sm)', padding: 12, fontSize: '0.88rem', whiteSpace: 'pre-wrap' }}>
                            {caseItem.description}
                          </div>
                        </div>
                      )}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                          <label className="text-sm text-muted">Typ</label>
                          <div style={{ fontSize: '0.88rem' }}>{caseItem.case_type}</div>
                        </div>
                        <div>
                          <label className="text-sm text-muted">Priorität</label>
                          <div style={{ fontSize: '0.88rem' }}>{caseItem.priority || '—'}</div>
                        </div>
                        <div>
                          <label className="text-sm text-muted">Startdatum</label>
                          <div style={{ fontSize: '0.88rem' }}>{caseItem.start_date ? fmtDate(caseItem.start_date) : '—'}</div>
                        </div>
                        <div>
                          <label className="text-sm text-muted">Enddatum</label>
                          <div style={{ fontSize: '0.88rem' }}>{caseItem.end_date ? fmtDate(caseItem.end_date) : '—'}</div>
                        </div>
                      </div>
                      <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border-light)', textAlign: 'right' }}>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={async () => {
                            if (!window.confirm(`Fall "${caseItem.title}" wirklich löschen? Alle zugehörigen Bausteine und Sitzungen gehen verloren.`)) return;
                            try {
                              await casesApi.delete(caseItem.id);
                              showToast('Fall wurde gelöscht.', 'success');
                              onChanged();
                            } catch {
                              showToast('Fall konnte nicht gelöscht werden.', 'error');
                            }
                          }}
                        >
                          <Trash2 size={12} /> Fall löschen
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Formular-Vorschau Modal */}
      {previewHtml && (
        <div className="modal-overlay" onClick={() => setPreviewHtml(null)}>
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 900, width: '95vw', height: '85vh', display: 'flex', flexDirection: 'column' }}
          >
            <div className="modal-header">
              <h2>Formular-Vorschau (so sieht es der Klient)</h2>
              <button className="btn-icon" onClick={() => setPreviewHtml(null)}><X size={20} /></button>
            </div>
            <div style={{ flex: 1, overflow: 'hidden', background: 'var(--bg)' }}>
              <iframe
                srcDoc={previewHtml}
                style={{ width: '100%', height: '100%', border: 'none' }}
                title="Formular-Vorschau"
              />
            </div>
            <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setPreviewHtml(null)}>
                Abbrechen
              </button>
              {client.email && previewBlockId && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    processApi.sendFillableForm(previewBlockId, {
                      client_email: client.email!,
                      client_name: `${client.first_name} ${client.last_name}`,
                    }).then((res) => {
                      showToast(res.data.message || 'Formular wurde gesendet.', 'success');
                      setPreviewHtml(null);
                      setPreviewBlockId(null);
                    }).catch(() => {
                      showToast('Senden fehlgeschlagen.', 'error');
                    });
                  }}
                >
                  <Send size={14} /> An {client.email} senden
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Tab: Dokumente (Placeholder)
// ============================================================

interface DocumentItem {
  id: string;
  file_name: string;
  file_type: string | null;
  file_size_bytes: number | null;
  document_type: string | null;
  title: string | null;
  client_id: string | null;
  case_id: string | null;
  created_at: string | null;
  source?: string;
  cloud_view_url?: string;
  cloud_file_id?: string;
}

const docTypeLabels: Record<string, string> = {
  protocol: 'Protokoll / Kopie',
  assessment: 'Formular',
  contract: 'Vertrag',
  analysis: 'Analyse',
};

// ============================================================
// Sitzungsliste: letzte Sitzung sichtbar, ältere aufklappbar
// ============================================================

function SessionsList({
  sessions,
  caseId,
  openSession,
  setOpenSession,
  client,
  showToast,
  loadedCasesRef,
  loadCaseSessions,
  onChanged,
}: {
  sessions: SessionItem[];
  caseId: string;
  openSession: SessionItem | null;
  setOpenSession: (s: SessionItem | null) => void;
  client: Client;
  showToast: (msg: string, type: 'success' | 'error' | 'info' | 'warning') => void;
  loadedCasesRef: React.MutableRefObject<Set<string>>;
  loadCaseSessions: (id: string) => void;
  onChanged: () => void;
}) {
  const [sessionFilter, setSessionFilter] = useState('all');
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [showCancelled, setShowCancelled] = useState(false);

  const sorted = [...sessions].sort((a, b) => (b.session_number || 0) - (a.session_number || 0));

  // Separate cancelled sessions
  const cancelledSessions = sorted.filter(s => s.status === 'cancelled');
  const activeSessions = sorted.filter(s => s.status !== 'cancelled');

  // Apply filter
  const filtered = sessionFilter === 'all' ? activeSessions
    : sessionFilter === 'completed' ? activeSessions.filter(s => s.status === 'completed')
    : sessionFilter === 'scheduled' ? activeSessions.filter(s => s.status === 'scheduled')
    : activeSessions;

  // Find next upcoming session
  const now = Date.now();
  const nextSession = activeSessions.find(s => s.status === 'scheduled' && s.scheduled_at && new Date(s.scheduled_at).getTime() > now);

  const toggleCardExpand = (id: string) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleExportPdf = async (sessionId: string) => {
    try {
      const res = await sessionsApi.exportNotesPdf(sessionId);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `sitzung_${sessionId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast('PDF-Export fehlgeschlagen.', 'error');
    }
  };

  const parseTopics = (topics: string | null): string[] => {
    if (!topics) return [];
    return topics.split(/[,;\n]/).map(t => t.trim()).filter(Boolean);
  };

  const renderSessionCard = (s: SessionItem) => {
    const isNext = nextSession?.id === s.id;
    const isExpanded = expandedCards.has(s.id);
    const isCancelled = s.status === 'cancelled';
    const topics = parseTopics(s.topics_discussed);
    const hasCoachNotes = s.coach_observations || s.next_session_notes;

    return (
      <div
        key={s.id}
        className={`pm-session-card ${isNext ? 'pm-session-card-next' : ''} ${isCancelled ? 'pm-session-card-cancelled' : ''}`}
      >
        {/* Timeline Node */}
        <div
          className="pm-session-node"
          style={{
            background: s.status === 'completed' ? 'var(--success, #2E8B57)' : s.status === 'scheduled' ? 'var(--primary)' : s.status === 'in_progress' ? 'var(--accent)' : 'var(--text-muted)',
          }}
        >
          {s.session_number}
        </div>

        {/* Card Header */}
        <div className="pm-session-card-header" onClick={() => toggleCardExpand(s.id)}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Sitzung {s.session_number}</span>
              {isNext && <span className="badge badge-new" style={{ fontSize: '0.68rem' }}>Nächster Termin</span>}
            </div>
            <div className="text-muted" style={{ fontSize: '0.8rem', marginTop: 2 }}>
              {s.scheduled_at ? new Date(s.scheduled_at).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Ohne Termin'}
              {s.scheduled_at && (
                <span style={{ marginLeft: 6 }}>
                  {new Date(s.scheduled_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
                </span>
              )}
              {s.duration_minutes ? <span style={{ marginLeft: 6 }}> · {s.duration_minutes} Min.</span> : null}
            </div>
          </div>
          <span className={`badge ${
            s.status === 'completed' ? 'badge-active' :
            s.status === 'in_progress' ? 'badge-ongoing' :
            s.status === 'scheduled' ? 'badge-new' :
            s.status === 'cancelled' ? 'badge-completed' : ''
          }`} style={{ flexShrink: 0, fontSize: '0.72rem' }}>
            {sessionStatusLabels[s.status] || s.status}
          </span>
          {isExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </div>

        {/* Card Body (expanded) */}
        {isExpanded && (
          <div className="pm-session-card-body">
            {/* Topics as Tags */}
            {topics.length > 0 && (
              <div>
                <label className="text-sm text-muted" style={{ marginBottom: 4, display: 'block' }}>Themen</label>
                <div className="pm-session-topics-tags">
                  {topics.map((t, i) => <span key={i} className="pm-session-topic-tag">{t}</span>)}
                </div>
              </div>
            )}

            {/* Homework */}
            {s.homework_assigned && (
              <div className="pm-session-homework">
                <ClipboardList size={15} />
                <div>
                  <strong style={{ fontSize: '0.8rem' }}>Hausaufgabe</strong>
                  <p style={{ margin: '2px 0 0', whiteSpace: 'pre-wrap' }}>{s.homework_assigned}</p>
                </div>
              </div>
            )}

            {/* Coach Notes (yellow, lock icon) */}
            {hasCoachNotes && (
              <div className="pm-session-coach-notes">
                <div className="pm-session-coach-notes-header">
                  <Lock size={12} /> Coach-Notizen (nur für dich sichtbar)
                </div>
                {s.coach_observations && <p><strong>Beobachtungen:</strong> {s.coach_observations}</p>}
                {s.next_session_notes && <p><strong>Für nächste Sitzung:</strong> {s.next_session_notes}</p>}
              </div>
            )}

            {/* Action Buttons */}
            <div className="pm-session-actions">
              <button className="btn btn-sm btn-secondary" onClick={() => setOpenSession(openSession?.id === s.id ? null : s)}>
                <Edit3 size={12} /> Protokoll öffnen
              </button>
              <button className="btn btn-sm btn-secondary" onClick={() => handleExportPdf(s.id)}>
                <Download size={12} /> PDF
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderProtocol = (s: SessionItem) => (
    <div className="card" style={{ marginBottom: 12, padding: 0, overflow: 'hidden' }}>
      <div className="flex-between" style={{ padding: '10px 16px', borderBottom: '1px solid var(--border-light)', background: 'var(--bg)' }}>
        <span style={{ fontWeight: 500, fontSize: '0.88rem' }}>
          Sitzungsprotokoll — Sitzung {s.session_number}
        </span>
        <button className="btn btn-sm btn-secondary" onClick={() => setOpenSession(null)}>
          <X size={14} /> Schließen
        </button>
      </div>
      <div style={{ padding: 16 }}>
        <SessionForm
          data={s.content_data || {}}
          blockId=""
          clientEmail={client.email || ''}
          sessionId={s.id}
          sessionNumber={s.session_number}
          sessionDate={s.scheduled_at || undefined}
          sessionDuration={s.duration_minutes || undefined}
          sessionFormat={s.location_type}
          sessionStatus={s.status}
          onSave={async (formData) => {
            await sessionsApi.update(s.id, { content_data: formData });
          }}
          onSessionUpdate={async (data) => {
            await sessionsApi.update(s.id, data);
            loadedCasesRef.current.delete(caseId);
            loadCaseSessions(caseId);
            onChanged();
          }}
          previousSessionNotes={(() => {
            const prev = sessions.find((ps) => ps.session_number === s.session_number - 1);
            if (!prev || !prev.content_data) return undefined;
            const d = prev.content_data;
            const parts: string[] = [];
            if (d.ergebnisse) parts.push(`Ergebnisse: ${d.ergebnisse}`);
            if (d.vereinbarungen) parts.push(`Vereinbarungen: ${d.vereinbarungen}`);
            if (d.hausaufgaben_naechste) parts.push(`Hausaufgaben: ${d.hausaufgaben_naechste}`);
            return parts.length > 0 ? parts.join('\n\n') : undefined;
          })()}
        />
      </div>
    </div>
  );

  const filters = [
    { key: 'all', label: 'Alle' },
    { key: 'completed', label: 'Abgeschlossen' },
    { key: 'scheduled', label: 'Geplant' },
  ];

  return (
    <>
      {/* Geöffnetes Sitzungsprotokoll */}
      {openSession && renderProtocol(openSession)}

      {/* Filter Pills */}
      <div className="pm-filter-pills">
        {filters.map(f => (
          <button
            key={f.key}
            className={`pm-filter-pill ${sessionFilter === f.key ? 'pm-filter-pill-active' : ''}`}
            onClick={() => setSessionFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Timeline */}
      <div className="pm-session-timeline">
        {filtered.map(s => renderSessionCard(s))}
        {filtered.length === 0 && (
          <div className="text-muted text-sm" style={{ padding: '8px 0' }}>Keine Sitzungen in dieser Auswahl.</div>
        )}
      </div>

      {/* Cancelled Sessions (collapsed group) */}
      {cancelledSessions.length > 0 && (
        <div className="pm-cancelled-group">
          <button className="pm-cancelled-group-toggle" onClick={() => setShowCancelled(!showCancelled)}>
            {showCancelled ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {cancelledSessions.length} stornierte Sitzung{cancelledSessions.length !== 1 ? 'en' : ''}
          </button>
          {showCancelled && (
            <div className="pm-session-timeline" style={{ marginTop: 4 }}>
              {cancelledSessions.map(s => renderSessionCard(s))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

const docTypeBadge: Record<string, string> = {
  protocol: 'badge-active',
  assessment: 'badge-ongoing',
  contract: 'badge-new',
  analysis: 'badge-completed',
};

function DokumenteTab({ clientId, refreshKey = 0 }: { clientId: string; refreshKey?: number }) {
  const { showToast } = useToast();
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [viewingDoc, setViewingDoc] = useState<string | null>(null);
  const [viewHtml, setViewHtml] = useState<string | null>(null);
  const [viewPdfUrl, setViewPdfUrl] = useState<string | null>(null);
  const [cloudPickerOpen, setCloudPickerOpen] = useState(false);
  const [googleConnected, setGoogleConnected] = useState(false);

  const loadDocuments = async () => {
    try {
      const res = await documentsApi.list({ client_id: clientId });
      setDocuments(res.data.items || []);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
    integrationsApi.googleStatus().then(res => setGoogleConnected(res.data.connected)).catch(() => {});
  }, [clientId, refreshKey]); // eslint-disable-line

  const handleDownload = async (doc: DocumentItem) => {
    setActionLoading(doc.id);
    try {
      const res = await documentsApi.download(doc.id);
      const isHtml = doc.file_type === 'html' || doc.file_name?.endsWith('.html');
      let blob: Blob;
      if (isHtml) {
        let text = typeof res.data === 'string' ? res.data : await new Blob([res.data]).text();
        text = injectThemeColors(text);
        blob = new Blob([text], { type: 'text/html;charset=utf-8' });
      } else {
        blob = new Blob([res.data]);
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      showToast('Download fehlgeschlagen.', 'error');
    }
    setActionLoading(null);
  };

  const closeViewer = () => {
    setViewingDoc(null);
    setViewHtml(null);
    if (viewPdfUrl) { window.URL.revokeObjectURL(viewPdfUrl); setViewPdfUrl(null); }
  };

  const injectThemeColors = (html: string): string => {
    const s = getComputedStyle(document.documentElement);
    const primary = s.getPropertyValue('--primary').trim();
    const primaryDark = s.getPropertyValue('--primary-dark').trim();
    const bg = s.getPropertyValue('--bg').trim();
    const border = s.getPropertyValue('--border').trim();
    if (!primary) return html;
    const overrides = `<style>
      h1 { color: ${primary} !important; border-bottom-color: ${primaryDark} !important; }
      h2 { color: ${primary} !important; border-bottom-color: ${border} !important; }
      .label { color: ${primary} !important; }
      .info-box { background: ${bg} !important; border-left-color: ${primaryDark} !important; }
      .signatures { border-top-color: ${primary} !important; }
      .sig-block p { color: ${primary} !important; }
      .footer { border-top-color: ${border} !important; }
      .no-print button { background: ${primaryDark} !important; }
    </style>`;
    return html.replace('</head>', overrides + '</head>');
  };

  const handleView = async (doc: DocumentItem) => {
    setActionLoading(doc.id);
    try {
      const res = await documentsApi.download(doc.id);
      const contentType = res.headers?.['content-type'] || 'application/octet-stream';
      const isHtml = doc.file_type === 'html' || doc.file_name?.endsWith('.html');
      const isPdf = contentType.includes('pdf') || doc.file_name?.endsWith('.pdf');
      closeViewer();
      if (isHtml) {
        let text = typeof res.data === 'string' ? res.data : await new Blob([res.data]).text();
        text = injectThemeColors(text);
        setViewHtml(text);
        setViewingDoc(doc.id);
      } else if (isPdf) {
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        setViewPdfUrl(url);
        setViewingDoc(doc.id);
      } else {
        const blob = new Blob([res.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      }
    } catch {
      showToast('Dokument konnte nicht geöffnet werden.', 'error');
    }
    setActionLoading(null);
  };

  const handleDelete = async (doc: DocumentItem) => {
    if (!window.confirm(`Dokument "${doc.file_name}" wirklich löschen?`)) return;
    setActionLoading(doc.id);
    try {
      await documentsApi.delete(doc.id);
      loadDocuments();
    } catch {
      showToast('Löschen fehlgeschlagen.', 'error');
    }
    setActionLoading(null);
  };

  const fmtSize = (bytes: number | null) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
        Dokumente werden geladen...
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="empty-state card">
        <FileText size={48} style={{ color: 'var(--primary-light)', marginBottom: 16 }} />
        <h3 style={{ marginBottom: 8 }}>Keine Dokumente</h3>
        <p className="text-muted">
          Dokumente werden automatisch erstellt, wenn Sie Formulare oder Kopien an den Klienten senden.
        </p>
        {googleConnected && (
          <button className="btn btn-sm btn-secondary" style={{ marginTop: 12 }} onClick={() => setCloudPickerOpen(true)}>
            <Cloud size={14} /> Aus Google Drive importieren
          </button>
        )}
        <CloudFilePicker
          open={cloudPickerOpen}
          onClose={() => setCloudPickerOpen(false)}
          clientId={clientId}
          onFileLinked={() => loadDocuments()}
          onFileImported={() => loadDocuments()}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="flex-between mb-16">
        <h3>Dokumente ({documents.length})</h3>
        {googleConnected && (
          <button className="btn btn-sm btn-secondary" onClick={() => setCloudPickerOpen(true)}>
            <Cloud size={14} /> Aus Google Drive
          </button>
        )}
      </div>
      <CloudFilePicker
        open={cloudPickerOpen}
        onClose={() => setCloudPickerOpen(false)}
        clientId={clientId}
        onFileLinked={() => loadDocuments()}
        onFileImported={() => loadDocuments()}
      />
      <div className="invoice-list">
        {/* Document Viewer (HTML + PDF) */}
        {viewingDoc && (viewHtml || viewPdfUrl) && (
          <div className="card" style={{ marginBottom: 16, padding: 0, overflow: 'hidden' }}>
            <div className="flex-between" style={{ padding: '12px 20px', borderBottom: '1px solid var(--border-light)', background: 'var(--bg)' }}>
              <span style={{ fontWeight: 500, fontSize: '0.9rem' }}>Dokumentvorschau</span>
              <div className="flex gap-8">
                {viewHtml && (
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => {
                      const win = window.open('', '_blank');
                      if (win) { win.document.write(viewHtml); win.document.close(); }
                    }}
                    title="In neuem Tab öffnen"
                  >
                    <Eye size={14} /> Neuer Tab
                  </button>
                )}
                {viewPdfUrl && (
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => window.open(viewPdfUrl, '_blank')}
                    title="In neuem Tab öffnen"
                  >
                    <Eye size={14} /> Neuer Tab
                  </button>
                )}
                <button className="btn btn-sm btn-secondary" onClick={closeViewer}>
                  <X size={14} /> Schließen
                </button>
              </div>
            </div>
            {viewHtml && (
              <iframe
                srcDoc={viewHtml}
                style={{ width: '100%', height: 600, border: 'none' }}
                title="Dokumentvorschau"
              />
            )}
            {viewPdfUrl && (
              <iframe
                src={viewPdfUrl}
                style={{ width: '100%', height: 600, border: 'none' }}
                title="PDF-Vorschau"
              />
            )}
          </div>
        )}

        {documents.map((doc) => {
          const isLoading = actionLoading === doc.id;
          return (
            <div key={doc.id} className="card" style={{ marginBottom: 8, padding: '14px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {doc.source === 'google_drive' ? (
                  <Cloud size={20} style={{ color: '#4285F4', flexShrink: 0 }} />
                ) : (
                  <FileText size={20} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {doc.title || doc.file_name}
                  </div>
                  <div className="text-muted text-sm" style={{ display: 'flex', gap: 12, marginTop: 2 }}>
                    <span>{doc.created_at ? fmtDate(doc.created_at) : '—'}</span>
                    <span>{fmtSize(doc.file_size_bytes)}</span>
                    <span>{doc.file_type?.toUpperCase() || '—'}</span>
                    {doc.source === 'google_drive' && <span style={{ color: '#4285F4' }}>Google Drive</span>}
                  </div>
                </div>
                <span className={`badge ${docTypeBadge[doc.document_type || ''] || 'badge-completed'}`}>
                  {docTypeLabels[doc.document_type || ''] || doc.document_type || 'Dokument'}
                </span>
                <div className="flex gap-8">
                  {doc.cloud_view_url ? (
                    <a
                      href={doc.cloud_view_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-primary"
                      title="In Google Drive öffnen"
                    >
                      <ExternalLink size={14} /> In Drive
                    </a>
                  ) : (
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleView(doc)}
                      disabled={isLoading}
                      title="Anzeigen"
                    >
                      <Eye size={14} /> Anzeigen
                    </button>
                  )}
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleDownload(doc)}
                    disabled={isLoading}
                    title="Herunterladen"
                  >
                    <Download size={14} />
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleDelete(doc)}
                    disabled={isLoading}
                    title="Löschen"
                    style={{ color: 'var(--danger)' }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// Tab: Rechnungen
// ============================================================

function RechnungenTab({
  clientId,
  invoices,
  summary,
  onReload,
}: {
  clientId: string;
  invoices: Invoice[];
  summary: InvoicesSummary | undefined;
  onReload: () => void;
}) {
  const { showToast } = useToast();
  const [expandedInvoice, setExpandedInvoice] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const handleSend = async (inv: Invoice) => {
    if (!window.confirm(`Rechnung ${inv.invoice_number} per E-Mail senden?`)) return;
    setActionLoading(inv.id);
    try {
      await invoicesApi.send(inv.id);
      showToast('Rechnung wurde gesendet.', 'success');
      onReload();
    } catch {
      showToast('Senden fehlgeschlagen.', 'error');
    }
    setActionLoading(null);
  };

  const handleReminder = async (inv: Invoice) => {
    if (!window.confirm(`Zahlungserinnerung für ${inv.invoice_number} senden?`)) return;
    setActionLoading(inv.id);
    try {
      await invoicesApi.sendReminder(inv.id);
      showToast('Zahlungserinnerung wurde gesendet.', 'success');
      onReload();
    } catch {
      showToast('Erinnerung konnte nicht gesendet werden.', 'error');
    }
    setActionLoading(null);
  };

  const handleDownloadPdf = async (inv: Invoice) => {
    setActionLoading(inv.id);
    try {
      const res = await invoicesApi.downloadPdf(inv.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${inv.invoice_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      showToast('PDF-Download fehlgeschlagen.', 'error');
    }
    setActionLoading(null);
  };

  const handleMarkPaid = async (inv: Invoice) => {
    if (!window.confirm(`Rechnung ${inv.invoice_number} als bezahlt markieren?`)) return;
    setActionLoading(inv.id);
    try {
      await invoicesApi.addPayment(inv.id, {
        amount_eur: inv.total_eur,
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'bank_transfer',
      });
      onReload();
    } catch {
      showToast('Zahlung konnte nicht eingetragen werden.', 'error');
    }
    setActionLoading(null);
  };

  const handleCancel = async (inv: Invoice) => {
    if (!window.confirm(`Rechnung ${inv.invoice_number} wirklich stornieren?`)) return;
    setActionLoading(inv.id);
    try {
      await invoicesApi.cancel(inv.id);
      onReload();
    } catch {
      showToast('Stornierung fehlgeschlagen.', 'error');
    }
    setActionLoading(null);
  };

  return (
    <div>
      {/* Summary */}
      {summary && summary.total > 0 && (
        <div className="card mb-16" style={{ padding: 16 }}>
          <div className="flex gap-16">
            <div>
              <span className="text-sm text-muted">Gesamt</span>
              <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>{fmtEur(summary.total_eur)}</div>
            </div>
            <div>
              <span className="text-sm text-muted">Rechnungen</span>
              <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>{summary.total}</div>
            </div>
            {summary.open > 0 && (
              <div>
                <span className="text-sm text-muted">Offen</span>
                <div style={{ fontWeight: 600, fontSize: '1.1rem', color: 'var(--warning)' }}>{summary.open}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Neue Rechnung Button */}
      <div className="mb-16">
        <Link to={`/rechnungen?client_id=${clientId}`} className="btn btn-primary">
          <Plus size={16} /> Neue Rechnung erstellen
        </Link>
      </div>

      {invoices.length === 0 ? (
        <div className="empty-state card">
          <Receipt size={48} style={{ color: 'var(--primary-light)', marginBottom: 16 }} />
          <h3 style={{ marginBottom: 8 }}>Keine Rechnungen</h3>
          <p className="text-muted">Für diesen Klienten wurden noch keine Rechnungen erstellt.</p>
        </div>
      ) : (
        <div className="invoice-list">
          {invoices.map((inv) => {
            const isExpanded = expandedInvoice === inv.id;
            const isLoading = actionLoading === inv.id;
            const canSend = inv.status === 'draft';
            const canRemind = inv.status === 'sent' || inv.status === 'overdue';
            const canPay = inv.status !== 'paid' && inv.status !== 'cancelled';
            const canCancel = inv.status !== 'paid' && inv.status !== 'cancelled';

            return (
              <div key={inv.id} className="card" style={{ marginBottom: 8, padding: 0, overflow: 'hidden' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '14px 20px',
                    cursor: 'pointer',
                    gap: 16,
                  }}
                  onClick={() => setExpandedInvoice(isExpanded ? null : inv.id)}
                >
                  <div style={{ flex: '0 0 100px', fontFamily: 'monospace', fontSize: '0.88rem' }}>
                    {inv.invoice_number}
                  </div>
                  <div style={{ flex: 1, fontWeight: 500 }}>
                    {inv.subject || '---'}
                  </div>
                  <div className="text-light text-sm" style={{ flex: '0 0 90px' }}>
                    {fmtDate(inv.invoice_date)}
                  </div>
                  <div style={{ flex: '0 0 90px', fontWeight: 600, textAlign: 'right' }}>
                    {fmtEur(inv.total_eur)}
                  </div>
                  <div style={{ flex: '0 0 100px', textAlign: 'center' }}>
                    <span className={`badge ${invoiceStatusBadge[inv.status] || ''}`}>
                      {invoiceStatusLabels[inv.status] || inv.status}
                    </span>
                  </div>
                  <div style={{ flex: '0 0 24px' }}>
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </div>

                {isExpanded && (
                  <div style={{
                    padding: '12px 20px 16px',
                    borderTop: '1px solid var(--border-light)',
                    background: 'var(--bg)',
                  }}>
                    <div className="flex gap-16 mb-12" style={{ fontSize: '0.88rem' }}>
                      <div>
                        <span className="text-muted">Fällig am: </span>
                        <span>{fmtDate(inv.due_date)}</span>
                      </div>
                      <div>
                        <span className="text-muted">Betrag: </span>
                        <span style={{ fontWeight: 600 }}>{fmtEur(inv.total_eur)}</span>
                      </div>
                    </div>
                    <div className="flex gap-8" style={{ flexWrap: 'wrap' }}>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={(e) => { e.stopPropagation(); handleDownloadPdf(inv); }}
                        disabled={isLoading}
                      >
                        <Download size={14} /> PDF
                      </button>

                      {canSend && (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={(e) => { e.stopPropagation(); handleSend(inv); }}
                          disabled={isLoading}
                        >
                          <Send size={14} /> Senden
                        </button>
                      )}

                      {canRemind && (
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={(e) => { e.stopPropagation(); handleReminder(inv); }}
                          disabled={isLoading}
                          style={{ color: 'var(--warning)' }}
                        >
                          <Bell size={14} /> Erinnerung
                        </button>
                      )}

                      {canPay && (
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={(e) => { e.stopPropagation(); handleMarkPaid(inv); }}
                          disabled={isLoading}
                          style={{ color: 'var(--success)' }}
                        >
                          <CheckCircle2 size={14} /> Bezahlt
                        </button>
                      )}

                      {canCancel && (
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={(e) => { e.stopPropagation(); handleCancel(inv); }}
                          disabled={isLoading}
                          style={{ color: 'var(--danger, #CD5C5C)' }}
                        >
                          <X size={14} /> Stornieren
                        </button>
                      )}

                      <Link
                        to="/rechnungen"
                        className="btn btn-sm btn-secondary"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Eye size={14} /> Zur Rechnungsseite
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ============================================================
// Tab: Portal
// ============================================================

interface PortalStatus {
  is_active: boolean;
  has_portal_access: boolean;
  email: string | null;
  last_login_at: string | null;
  settings: {
    client_id: string;
    show_sessions: boolean;
    show_documents: boolean;
    show_homework: boolean;
    show_followups: boolean;
    show_invoices: boolean;
    show_bookings: boolean;
    show_support?: boolean;
  } | null;
}

// ============================================================
// Pakete-Tab — Zugewiesene Pakete + neue Zuweisung
// ============================================================

function PaketeTab({ clientId }: { clientId: string }) {
  const { showToast } = useToast();
  const [clientPkgs, setClientPkgs] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAssign, setShowAssign] = useState(false);
  const [assignForm, setAssignForm] = useState({ package_id: '', price_paid_eur: '', notes: '', billing_method: 'full', installment_count: '3' });
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const handlePackageAction = async (cpId: string, action: 'pause' | 'resume' | 'cancel' | 'next_rate' | 'bill_usage') => {
    setActionLoading(`${cpId}-${action}`);
    try {
      if (action === 'pause') {
        await packagesApi.pausePackage(cpId);
        showToast('Paket pausiert', 'success');
      } else if (action === 'resume') {
        await packagesApi.resumePackage(cpId);
        showToast('Paket reaktiviert', 'success');
      } else if (action === 'cancel') {
        if (!window.confirm('Paket wirklich stornieren? Diese Aktion kann nicht rückgängig gemacht werden.')) {
          setActionLoading(null);
          return;
        }
        await packagesApi.cancelPackage(cpId);
        showToast('Paket storniert', 'success');
      } else if (action === 'next_rate') {
        await invoicesApi.fromPackage({ client_package_id: cpId, billing_trigger: 'installment' });
        showToast('Nächste Rate erstellt', 'success');
      } else if (action === 'bill_usage') {
        await invoicesApi.fromPackage({ client_package_id: cpId, billing_trigger: 'usage' });
        showToast('Sitzungen abgerechnet', 'success');
      }
      loadData();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler bei Aktion', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [cpRes, tplRes] = await Promise.all([
        packagesApi.clientPackages({ client_id: clientId }),
        packagesApi.list(),
      ]);
      setClientPkgs(cpRes.data);
      setTemplates(tplRes.data.filter((t: any) => t.is_active));
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!assignForm.package_id) {
      showToast('Bitte ein Paket auswählen', 'error');
      return;
    }
    setSaving(true);
    try {
      await packagesApi.assignPackage({
        client_id: clientId,
        package_id: assignForm.package_id,
        price_paid_eur: assignForm.price_paid_eur ? parseFloat(String(assignForm.price_paid_eur).replace(',', '.')) : undefined,
        billing_method: assignForm.billing_method,
        installment_count: assignForm.billing_method === 'installments' ? parseInt(assignForm.installment_count) || 3 : undefined,
        notes: assignForm.notes || undefined,
      });
      const billingLabels: Record<string, string> = { full: 'Gesamtrechnung erstellt', installments: 'Rate 1 erstellt', per_usage: '' };
      const extra = billingLabels[assignForm.billing_method] || '';
      showToast(`Paket zugewiesen${extra ? ` — ${extra}` : ''}`, 'success');
      setShowAssign(false);
      setAssignForm({ package_id: '', price_paid_eur: '', notes: '', billing_method: 'full', installment_count: '3' });
      loadData();
    } catch { showToast('Fehler beim Zuweisen', 'error'); }
    finally { setSaving(false); }
  };

  const selectedTpl = templates.find((t: any) => t.id === assignForm.package_id);

  if (loading) return (
    <div className="card" style={{ padding: 32 }}>
      <Skeleton width="40%" height={14} />
      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Skeleton height={60} />
        <Skeleton height={60} />
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Coaching-Pakete</h3>
        {!showAssign && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowAssign(true)}>
            <Plus size={14} /> Paket zuweisen
          </button>
        )}
      </div>

      {/* Zuweisungs-Formular */}
      {showAssign && (
        <form onSubmit={handleAssign} className="card" style={{ padding: 20, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h4 style={{ margin: 0 }}>Paket zuweisen</h4>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowAssign(false)}><X size={16} /></button>
          </div>
          <div className="form-group">
            <label>Paket-Vorlage *</label>
            <select value={assignForm.package_id} onChange={(e) => {
              const tpl = templates.find((t: any) => t.id === e.target.value);
              setAssignForm({ ...assignForm, package_id: e.target.value, price_paid_eur: tpl ? String(tpl.price_eur).replace('.', ',') : '' });
            }} required>
              <option value="">— Paket wählen —</option>
              {(() => {
                const grouped: Record<string, any[]> = {};
                templates.forEach((t: any) => {
                  const cat = t.category || 'Sonstige';
                  if (!grouped[cat]) grouped[cat] = [];
                  grouped[cat].push(t);
                });
                return Object.entries(grouped).map(([cat, pkgs]) => (
                  <optgroup key={cat} label={cat}>
                    {pkgs.map((t: any) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.session_count} Sitzungen, {t.price_eur.toFixed(2)} EUR)
                      </option>
                    ))}
                  </optgroup>
                ));
              })()}
            </select>
          </div>
          {selectedTpl && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label>Bezahlter Preis (EUR)</label>
                  <input value={assignForm.price_paid_eur} onChange={(e) => setAssignForm({ ...assignForm, price_paid_eur: e.target.value.replace(/[^0-9.,]/g, '') })} placeholder="z.B. 2000,00" />
                </div>
                <div className="form-group">
                  <label>Abrechnungsmodell</label>
                  <select value={assignForm.billing_method} onChange={(e) => setAssignForm({ ...assignForm, billing_method: e.target.value })}>
                    <option value="full">Gesamtrechnung</option>
                    <option value="installments">Ratenzahlung</option>
                    <option value="per_usage">Nach Verbrauch</option>
                  </select>
                </div>
              </div>
              {assignForm.billing_method === 'installments' && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Anzahl Raten</label>
                    <input type="number" value={assignForm.installment_count} onChange={(e) => setAssignForm({ ...assignForm, installment_count: e.target.value })} min={2} max={12} style={{ width: 100 }} />
                  </div>
                  <div className="form-group">
                    <label>Ratenbetrag</label>
                    <input type="text" readOnly style={{ background: 'var(--bg)', color: 'var(--text-muted)', width: 160 }}
                      value={`${(parseFloat(String(assignForm.price_paid_eur).replace(',', '.') || '0') / (parseInt(assignForm.installment_count) || 3)).toFixed(2)} EUR x ${assignForm.installment_count}`}
                    />
                  </div>
                </div>
              )}
              <div className="form-group">
                <label>Notiz</label>
                <input value={assignForm.notes} onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })} placeholder="Optional..." />
              </div>
            </>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
              <Save size={14} /> {saving ? 'Zuweisen...' : 'Zuweisen'}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowAssign(false)}>Abbrechen</button>
          </div>
        </form>
      )}

      {/* Paket-Liste */}
      {clientPkgs.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <p className="text-muted">Diesem Klienten wurden noch keine Pakete zugewiesen.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {clientPkgs.map((cp: any) => {
            const pct = cp.sessions_total > 0 ? Math.round((cp.sessions_used / cp.sessions_total) * 100) : 0;
            const remainPct = cp.sessions_total > 0 ? (cp.sessions_remaining / cp.sessions_total) * 100 : 0;
            const barColor = cp.status !== 'active' ? 'var(--text-muted)' : remainPct > 50 ? '#2e7d32' : remainPct > 20 ? '#B8860B' : '#dc2626';
            const statusColors: Record<string, string> = { active: '#2e7d32', completed: 'var(--primary)', expired: '#991b1b', paused: '#B8860B', cancelled: '#991b1b' };
            const statusColor = statusColors[cp.status] || 'var(--text-muted)';
            const statusLabel: Record<string, string> = { active: 'Aktiv', completed: 'Abgeschlossen', expired: 'Abgelaufen', paused: 'Pausiert', cancelled: 'Storniert' };
            const billingLabels: Record<string, string> = { full: 'Gesamtrechnung', installments: 'Ratenzahlung', per_usage: 'Nach Verbrauch' };
            const price = parseFloat(cp.price_paid_eur) || 0;
            const invoiced = parseFloat(cp.amount_invoiced) || 0;
            const paid = parseFloat(cp.amount_paid) || 0;
            const invoicedPct = price > 0 ? Math.min(100, Math.round((invoiced / price) * 100)) : 0;
            const paidPct = price > 0 ? Math.min(100, Math.round((paid / price) * 100)) : 0;
            return (
              <div key={cp.id} className="card" style={{ padding: '16px 20px' }}>
                {/* Header: Name + Badges */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
                  <strong>{cp.package_name}</strong>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {cp.billing_method && (
                      <span style={{ fontSize: '0.7rem', fontWeight: 500, color: 'var(--text-muted)', background: 'var(--bg)', padding: '2px 8px', borderRadius: 4 }}>
                        {billingLabels[cp.billing_method] || cp.billing_method}
                        {cp.billing_method === 'installments' && cp.installment_count ? ` (${cp.installment_count}x)` : ''}
                      </span>
                    )}
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: statusColor, background: `${statusColor}15`, padding: '2px 8px', borderRadius: 4 }}>
                      {statusLabel[cp.status] || cp.status}
                    </span>
                  </div>
                </div>

                {/* Sitzungs-Fortschritt */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                  <div style={{ flex: 1, height: 8, background: 'var(--bg)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: 4, transition: 'width 0.3s' }} />
                  </div>
                  <span className="text-sm" style={{ fontWeight: 600, minWidth: 80, textAlign: 'right' }}>
                    {cp.sessions_used} / {cp.sessions_total} genutzt
                  </span>
                </div>

                {/* Zahlungs-Fortschritt */}
                {price > 0 && (
                  <div style={{ marginBottom: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ flex: 1, height: 6, background: 'var(--bg)', borderRadius: 3, overflow: 'hidden', position: 'relative' }}>
                        <div style={{ width: `${invoicedPct}%`, height: '100%', background: '#b0bec5', borderRadius: 3, position: 'absolute', top: 0, left: 0, transition: 'width 0.3s' }} />
                        <div style={{ width: `${paidPct}%`, height: '100%', background: '#2e7d32', borderRadius: 3, position: 'absolute', top: 0, left: 0, transition: 'width 0.3s' }} />
                      </div>
                      <span className="text-sm" style={{ minWidth: 120, textAlign: 'right', color: 'var(--text-muted)' }}>
                        {paid.toFixed(0)} / {price.toFixed(0)} EUR
                      </span>
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.7rem', display: 'flex', gap: 12, marginTop: 2 }}>
                      <span>Berechnet: {invoiced.toFixed(2)} EUR</span>
                      <span>Bezahlt: {paid.toFixed(2)} EUR</span>
                    </div>
                  </div>
                )}

                {/* Metadaten */}
                <div className="text-muted text-sm" style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <span>{cp.sessions_remaining} verbleibend</span>
                  <span>{price.toFixed(2)} EUR</span>
                  {cp.valid_until && <span>Gültig bis: {new Date(cp.valid_until).toLocaleDateString('de-DE')}</span>}
                  {cp.purchased_at && <span>Zugewiesen: {new Date(cp.purchased_at).toLocaleDateString('de-DE')}</span>}
                </div>
                {cp.notes && <p className="text-muted text-sm" style={{ margin: '4px 0 0', fontStyle: 'italic' }}>{cp.notes}</p>}

                {/* Action-Buttons */}
                {cp.status !== 'cancelled' && cp.status !== 'completed' && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                    {cp.status === 'active' && (
                      <button className="btn btn-ghost btn-sm" disabled={actionLoading === `${cp.id}-pause`}
                        onClick={() => handlePackageAction(cp.id, 'pause')}>
                        Pausieren
                      </button>
                    )}
                    {cp.status === 'paused' && (
                      <button className="btn btn-ghost btn-sm" disabled={actionLoading === `${cp.id}-resume`}
                        onClick={() => handlePackageAction(cp.id, 'resume')}>
                        Reaktivieren
                      </button>
                    )}
                    {cp.billing_method === 'installments' && cp.status === 'active' && (
                      <button className="btn btn-secondary btn-sm" disabled={actionLoading === `${cp.id}-next_rate`}
                        onClick={() => handlePackageAction(cp.id, 'next_rate')}>
                        <Receipt size={13} /> Nächste Rate
                      </button>
                    )}
                    {cp.billing_method === 'per_usage' && cp.status === 'active' && cp.sessions_used > 0 && (
                      <button className="btn btn-secondary btn-sm" disabled={actionLoading === `${cp.id}-bill_usage`}
                        onClick={() => handlePackageAction(cp.id, 'bill_usage')}>
                        <Receipt size={13} /> Sitzungen abrechnen
                      </button>
                    )}
                    {(cp.status === 'active' || cp.status === 'paused') && (
                      <button className="btn btn-ghost btn-sm" style={{ color: '#991b1b' }}
                        disabled={actionLoading === `${cp.id}-cancel`}
                        onClick={() => handlePackageAction(cp.id, 'cancel')}>
                        Stornieren
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PortalTab({ clientId, clientEmail }: { clientId: string; clientEmail: string | null }) {
  const { showToast } = useToast();
  const [status, setStatus] = useState<PortalStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMsg, setInviteMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const res = await portalApi.getClientStatus(clientId);
      setStatus(res.data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInvite = async () => {
    setSaving(true);
    setInviteMsg('');
    try {
      const res = await portalApi.invite({ client_id: clientId, email: inviteEmail || undefined });
      setInviteMsg(res.data.message || 'Einladung gesendet');
      setInviteEmail('');
      loadStatus();
    } catch (err: any) {
      setInviteMsg(err.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    setSaving(true);
    try {
      if (status?.is_active) {
        await portalApi.deactivatePortal(clientId);
      } else {
        await portalApi.activatePortal(clientId);
      }
      loadStatus();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSettingChange = async (field: string, value: boolean) => {
    try {
      await portalApi.updateSettings(clientId, { [field]: value });
      loadStatus();
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Fehler beim Speichern', 'error');
    }
  };

  if (loading) return <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Wird geladen...</div>;

  const fmtDateTime = (d: string) => new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const settingsFields = [
    { key: 'show_sessions', label: 'Sitzungen' },
    { key: 'show_documents', label: 'Dokumente' },
    { key: 'show_homework', label: 'Hausaufgaben' },
    { key: 'show_followups', label: 'Follow-ups' },
    { key: 'show_invoices', label: 'Rechnungen' },
    { key: 'show_bookings', label: 'Termine/Buchungen' },
    { key: 'show_support', label: 'Hilfe / Support' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Status */}
      <div className="card" style={{ padding: 20 }}>
        <h3 style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Globe size={18} /> Portal-Status
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div>
            <strong>Zugang:</strong>{' '}
            {status?.has_portal_access ? (
              <span className="badge badge-active">Registriert</span>
            ) : (
              <span className="badge badge-new">Kein Zugang</span>
            )}
          </div>
          {status?.has_portal_access && (
            <>
              <div>
                <strong>Status:</strong>{' '}
                {status.is_active ? (
                  <span className="badge badge-active">Aktiv</span>
                ) : (
                  <span className="badge badge-paused">Deaktiviert</span>
                )}
              </div>
              <div><strong>E-Mail:</strong> {status.email || '\u2014'}</div>
              <div><strong>Letzter Login:</strong> {status.last_login_at ? fmtDateTime(status.last_login_at) : 'Noch nie'}</div>
            </>
          )}
        </div>

        {/* Activate / Deactivate */}
        {status?.has_portal_access && (
          <div style={{ marginTop: 12 }}>
            <button
              className={`btn btn-sm ${status.is_active ? 'btn-secondary' : 'btn-primary'}`}
              onClick={handleToggleActive}
              disabled={saving}
            >
              {status.is_active ? 'Portal deaktivieren' : 'Portal aktivieren'}
            </button>
          </div>
        )}
      </div>

      {/* Invitation */}
      {!status?.has_portal_access && (
        <div className="card" style={{ padding: 20 }}>
          <h3 style={{ marginBottom: 12 }}>Einladung senden</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <label className="text-sm">E-Mail (leer = Klienten-E-Mail)</label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={clientEmail || 'E-Mail-Adresse'}
              />
            </div>
            <button className="btn btn-primary" onClick={handleInvite} disabled={saving}>
              <Send size={14} /> Einladung senden
            </button>
          </div>
          {inviteMsg && <p className="text-sm" style={{ marginTop: 8, color: 'var(--primary-dark)' }}>{inviteMsg}</p>}
        </div>
      )}

      {/* Visibility Settings */}
      <div className="card" style={{ padding: 20 }}>
        <h3 style={{ marginBottom: 12 }}>Sichtbarkeit im Portal</h3>
        <p className="text-muted text-sm" style={{ marginBottom: 16 }}>
          Steuern Sie, welche Bereiche der Klient im Portal sehen kann.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {settingsFields.map((f) => {
            const checked = status?.settings ? (status.settings as any)[f.key] : true;
            return (
              <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '8px 12px', background: checked ? '#f0f8f0' : '#fafafa', borderRadius: 8, border: `1px solid ${checked ? '#c3e6c3' : '#eee'}` }}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => handleSettingChange(f.key, e.target.checked)}
                  style={{ width: 18, height: 18 }}
                />
                <span style={{ fontSize: '0.9rem' }}>{f.label}</span>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}
